/**
 * MindMap Component
 * Main component with React Flow canvas and profile management
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from "@mui/material";
import { ReactFlowProvider } from "@xyflow/react";
import SaveIcon from "@mui/icons-material/Save";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import DeleteIcon from "@mui/icons-material/Delete";
import CategoryIcon from "@mui/icons-material/Category";
import ClearIcon from "@mui/icons-material/Clear";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MindMapCanvas from "./MindMapCanvas";
import MindMapSidebar from "./MindMapSidebar";
import MindMapTemplates from "./MindMapTemplates";
import { DnDProvider } from "./DnDContext";
import {
  MindMapProfile,
  MindMapTemplate,
  MindMapNode,
  MindMapEdge,
  DEFAULT_NODES,
  DEFAULT_EDGES,
} from "./mindMapTypes";
import {
  loadProfiles,
  saveProfiles,
  createProfile,
  updateProfile,
  deleteProfile,
  findProfile,
  loadDraft,
  saveDraft,
  clearDraft,
} from "./mindMapUtils";

const SELECTED_PROFILE_STORAGE_KEY = "mindmap_selected_profile_v1";

const MindMap: React.FC = () => {
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const autosaveTimerRef = useRef<number | null>(null);

  // Node and edge state
  const [nodes, setNodes] = useState<MindMapNode[]>(DEFAULT_NODES);
  const [edges, setEdges] = useState<MindMapEdge[]>(DEFAULT_EDGES);

  // Profile state
  const [profiles, setProfiles] = useState<MindMapProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>("");
  const [isDirty, setIsDirty] = useState<boolean>(false);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // Dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState<boolean>(false);
  const [templatesDialogOpen, setTemplatesDialogOpen] =
    useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [profileName, setProfileName] = useState<string>("");

  // Load profiles on mount and check URL for profile ID
  useEffect(() => {
    const savedProfiles = loadProfiles();
    setProfiles(savedProfiles);

    // Check URL for profile ID
    const params = new URLSearchParams(window.location.search);
    const profileIdFromUrl = params.get("mindmap");
    if (profileIdFromUrl) {
      const profile = findProfile(savedProfiles, profileIdFromUrl);
      if (profile) {
        setSelectedProfileId(profileIdFromUrl);
        try {
          localStorage.setItem(SELECTED_PROFILE_STORAGE_KEY, profileIdFromUrl);
        } catch {
          // ignore
        }
        setNodes(profile.nodes);
        setEdges(profile.edges);
        setIsDirty(false);
        return;
      }
    }

    // No URL profile: restore last selected profile (if present).
    try {
      const last = localStorage.getItem(SELECTED_PROFILE_STORAGE_KEY);
      if (last) {
        const profile = findProfile(savedProfiles, last);
        if (profile) {
          setSelectedProfileId(last);
          updateUrl(last);
          setNodes(profile.nodes);
          setEdges(profile.edges);
          setIsDirty(false);
          return;
        }
      }
    } catch {
      // ignore
    }

    // No profile selected: restore last autosaved draft (if any).
    const draft = loadDraft();
    if (draft?.nodes?.length) {
      setNodes(draft.nodes);
      setEdges(draft.edges || []);
      setIsDirty(false);
    }
  }, []);

  // Update URL when profile selection changes
  const updateUrl = useCallback((profileId: string) => {
    const url = new URL(window.location.href);
    if (profileId) {
      url.searchParams.set("mindmap", profileId);
    } else {
      url.searchParams.delete("mindmap");
    }
    window.history.replaceState({}, "", url.toString());
  }, []);

  // Listen for fullscreen changes (e.g., user presses Escape)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Track changes
  const handleNodesChange = useCallback((newNodes: MindMapNode[]) => {
    setNodes(newNodes);
    setIsDirty(true);
  }, []);

  const handleEdgesChange = useCallback((newEdges: MindMapEdge[]) => {
    setEdges(newEdges);
    setIsDirty(true);
  }, []);

  // Handle profile selection
  const handleProfileSelect = (profileId: string) => {
    setSelectedProfileId(profileId);
    updateUrl(profileId);
    try {
      if (profileId) {
        localStorage.setItem(SELECTED_PROFILE_STORAGE_KEY, profileId);
      } else {
        localStorage.removeItem(SELECTED_PROFILE_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
    const profile = findProfile(profiles, profileId);
    if (profile) {
      setNodes(profile.nodes);
      setEdges(profile.edges);
      setIsDirty(false);
    } else {
      setNodes(DEFAULT_NODES);
      setEdges(DEFAULT_EDGES);
      setIsDirty(false);
    }
  };

  // Save new profile
  const handleSaveNew = () => {
    if (!profileName.trim()) return;

    const newProfile = createProfile(profileName.trim(), nodes, edges);
    const updatedProfiles = [...profiles, newProfile];
    setProfiles(updatedProfiles);
    saveProfiles(updatedProfiles);
    setSelectedProfileId(newProfile.id);
    updateUrl(newProfile.id);
    try {
      localStorage.setItem(SELECTED_PROFILE_STORAGE_KEY, newProfile.id);
    } catch {
      // ignore
    }
    clearDraft();
    setIsDirty(false);
    setSaveDialogOpen(false);
    setProfileName("");
  };

  // Update existing profile
  const handleUpdateProfile = () => {
    if (!selectedProfileId) return;

    const updatedProfiles = updateProfile(profiles, selectedProfileId, {
      nodes,
      edges,
    });
    setProfiles(updatedProfiles);
    saveProfiles(updatedProfiles);
    setIsDirty(false);
  };

  // Delete profile
  const handleDeleteProfile = () => {
    if (!selectedProfileId) return;

    const updatedProfiles = deleteProfile(profiles, selectedProfileId);
    setProfiles(updatedProfiles);
    saveProfiles(updatedProfiles);
    setSelectedProfileId("");
    updateUrl("");
    try {
      localStorage.removeItem(SELECTED_PROFILE_STORAGE_KEY);
    } catch {
      // ignore
    }
    setNodes(DEFAULT_NODES);
    setEdges(DEFAULT_EDGES);
    clearDraft();
    setIsDirty(false);
    setDeleteDialogOpen(false);
  };

  // Handle template selection
  const handleTemplateSelect = (template: MindMapTemplate) => {
    setNodes(template.nodes);
    setEdges(template.edges);
    setSelectedProfileId("");
    updateUrl("");
    try {
      localStorage.removeItem(SELECTED_PROFILE_STORAGE_KEY);
    } catch {
      // ignore
    }
    setIsDirty(true);
  };

  // Clear canvas
  const handleClear = () => {
    setNodes(DEFAULT_NODES);
    setEdges(DEFAULT_EDGES);
    setSelectedProfileId("");
    updateUrl("");
    try {
      localStorage.removeItem(SELECTED_PROFILE_STORAGE_KEY);
    } catch {
      // ignore
    }
    setIsDirty(false);
    clearDraft();
  };

  // Autosave: update selected profile, otherwise save a local draft.
  useEffect(() => {
    if (!isDirty) return;

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    autosaveTimerRef.current = window.setTimeout(() => {
      if (selectedProfileId) {
        const updatedProfiles = updateProfile(profiles, selectedProfileId, {
          nodes,
          edges,
        });
        setProfiles(updatedProfiles);
        saveProfiles(updatedProfiles);
      } else {
        saveDraft(nodes, edges);
      }
      setIsDirty(false);
      autosaveTimerRef.current = null;
    }, 500);

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [edges, isDirty, nodes, profiles, selectedProfileId]);

  // Toggle fullscreen
  const handleFullscreenToggle = async () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      try {
        await containerRef.current.requestFullscreen();
      } catch (err) {
        console.error("Failed to enter fullscreen:", err);
      }
    } else {
      try {
        await document.exitFullscreen();
      } catch (err) {
        console.error("Failed to exit fullscreen:", err);
      }
    }
  };

  const selectedProfile = selectedProfileId
    ? findProfile(profiles, selectedProfileId)
    : null;

  return (
    <ReactFlowProvider>
      <DnDProvider>
        <Box
          ref={containerRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: isFullscreen ? "100vh" : "calc(100vh - 220px)",
            maxHeight: isFullscreen ? "100vh" : "calc(100vh - 220px)",
            overflow: "hidden",
            ...(isFullscreen && { bgcolor: "background.paper", p: 2 }),
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Mind Map
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag nodes from the sidebar. Connect by dragging from handles.
              Double-click to edit labels.
            </Typography>
          </Box>

          {/* Profile Management Bar */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Saved Mind Maps</InputLabel>
              <Select
                value={selectedProfileId}
                onChange={(e) => handleProfileSelect(e.target.value)}
                label="Saved Mind Maps"
              >
                <MenuItem value="">
                  <em>New Mind Map</em>
                </MenuItem>
                {profiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<SaveAsIcon />}
              onClick={() => setSaveDialogOpen(true)}
              size="small"
            >
              Save As
            </Button>

            {selectedProfile && (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleUpdateProfile}
                  disabled={!isDirty}
                  size="small"
                >
                  Update
                </Button>
                <Tooltip title="Delete this mind map">
                  <IconButton
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="outlined"
              startIcon={<CategoryIcon />}
              onClick={() => setTemplatesDialogOpen(true)}
              size="small"
            >
              Templates
            </Button>

            <Tooltip title="Clear and reset">
              <IconButton onClick={handleClear} size="small">
                <ClearIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
              <IconButton onClick={handleFullscreenToggle} size="small">
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Main Content - Sidebar + Canvas */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              minHeight: 0,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <MindMapSidebar />
            <MindMapCanvas
              initialNodes={nodes}
              initialEdges={edges}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              profileId={selectedProfileId || undefined}
            />
          </Box>

          {/* Save Dialog */}
          <Dialog
            open={saveDialogOpen}
            onClose={() => setSaveDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Save Mind Map</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Mind Map Name"
                fullWidth
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && profileName.trim()) {
                    handleSaveNew();
                  }
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSaveNew}
                variant="contained"
                disabled={!profileName.trim()}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
          >
            <DialogTitle>Delete Mind Map</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete "{selectedProfile?.name}"? This
                action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleDeleteProfile}
                color="error"
                variant="contained"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Templates Dialog */}
          <MindMapTemplates
            open={templatesDialogOpen}
            onClose={() => setTemplatesDialogOpen(false)}
            onSelect={handleTemplateSelect}
          />
        </Box>
      </DnDProvider>
    </ReactFlowProvider>
  );
};

export default MindMap;
